// ============================================================================
// Lifespan v1 — ParamSet (single source of truth for every constant).
// Mirrors GDD.md §10 "Parameter Reference". All falsifiable goals PASS at
// these values. History: v1.1.0 = the design-pass adversarial review (hOT
// 0.6->0.75, sabMinYears, debtRelease, progressEpsilon, micro-card repricing);
// v1.2.0 = the smoothed burnout step (burnoutM 20 -> the burnoutHi 24 /
// burnoutLo 12 capacity band; income + hBurn continuous in M — kills the
// reactive burnout-surf, enforced by G14). Evidence: design/PARAMETERS.md.
// ============================================================================

import type { Params } from './types.ts';

export const PARAMS: Params = {
  paramSetVersion: '1.3.0',

  // --- #1..#3 start state ---------------------------------------------------
  startAge: 18, // yr    — Stance §14.1
  maxAge: 120, // yr     — sim guard only; death is H<=0 (no lifeCap)
  S0: 1000, // $         — starting cash
  M0: 70, // pt          — starting morale
  H0: 100, // pt         — starting health

  // --- #4 savings -----------------------------------------------------------
  r: 0.03, // /yr        — interest on positive balances (continuous)
  rDebt: 0.08, // /yr    — overdraft interest
  debtFloor: -25000, // $ — below this, Living (incl. tuition) locks to $0
  debtRelease: -20000, // $ — Broke lock releases only above this (hysteresis)

  // --- #5 wages -------------------------------------------------------------
  wages: [20000, 32000, 50000, 78000, 122000, 190000, 260000], // $/yr, tier 0..6

  // --- #6 cost of living ----------------------------------------------------
  colBase: 12000, // $/yr
  colSlope: 0.1, // —     — (fraction of income)

  // --- #7 work-mode / study income multipliers -------------------------------
  modeMult: { sab: 0.25, std: 1.0, ot: 1.6 },
  studyMult: 0.75,

  // --- #8..#10 morale --------------------------------------------------------
  kM: 0.2, // /yr         — morale rate constant (5-year time constant)
  drain: { sab: 2, std: 10, ot: 25 }, // pt/yr
  studyDrain: 5, // pt/yr
  expCap: 15, // pt/yr     — max purchasable morale gain
  expScaleFrac: 0.15, // — — G = expCap*(1 - e^(-s/(expScaleFrac*wage)))

  // --- #11..#13 gates --------------------------------------------------------
  gateLo: 30, // pt        — study speed zero at/below
  gateSpan: 50, // pt      — full speed at gateLo+gateSpan (=80)
  enrollM: 70, // pt       — enrolment morale gate...
  enrollMSkills: [4], //   — ...applies to Skill 4 only (§12 audit)
  // Burnout capacity band (v1.2.0 — the smoothed step; Trevor's call 2026-07-22).
  // Capacity falls LINEARLY from 1.0 at M >= 24 to 0.75 at M <= 12: continuous
  // in M, so reacting to observed morale can never beat the equivalent open-loop
  // plan (no surfable discontinuity — philosophy #1, enforced by G14). Band
  // chosen to preserve the tuned economy: the top (24) sits below the
  // disciplined grinder's M* ~= 26 and the banker's M* = 25 (their lives are
  // unchanged — note the banker clears the band top by only 1.0 pt, a tuning
  // constraint on burnoutHi: any drain/expCap tweak or accepted earn cards can
  // push a banker-shaped life into the band); the floor (12) keeps the naive
  // grinder (M* = 0: OT drain 25, zero Living) at the full -25% penalty. hBurn
  // scales with the same fraction.
  burnoutHi: 24, // pt — capacity 1.0 at/above
  burnoutLo: 12, // pt — capacity floor at/below
  burnoutMult: 0.75, // — — the income-capacity floor

  // --- #14..#17 skills -------------------------------------------------------
  studyYears: [0, 2, 2, 3, 3, 4, 4], // yr (index = skill)
  tuition: [0, 3000, 6000, 12000, 25000, 50000, 100000], // $
  sabRequiredForSkills: [5, 6], // sabbatical time since previous skill completion
  sabMinYears: 0.75, // game-yr — cumulative sabbatical time required to satisfy
  //                              the Skill 5/6 prerequisite. Time-integrated,
  //                              not a latch: a 1-month sabbatical does NOT open
  //                              the gate (micro-sab exploit fix).
  progressDecayRate: 0.288, // /yr — p *= e^(-0.288*dt) = 25%/yr decay
  progressEpsilon: 0.05, // study-yr — paused progress decaying below this
  //                                   cancels the enrolment (tuition forfeit;
  //                                   the Degree Rusting fiction).
  dormantDecayEnabled: false, // ships disabled (v3 rail)

  // --- #18..#22 health -------------------------------------------------------
  hBase: 0.25, // pt/yr
  hQuad: 0.0009, // pt/yr^3 — *(age-18)^2
  hOT: 0.75, // pt/yr       — overtime erosion (sanctioned G9 lever; 0.6 -> 0.75
  //                          in v1.1.0 so end-grind lands strictly below 100%).
  hBurn: 0.5, // pt/yr      — misery erosion at the capacity floor (scales with burnoutFrac)
  // Flourishing band (v1.3.0 — the smoothed wellM step; Trevor's call
  // 2026-07-22). The health dividend scales linearly from 0 at M <= 64 to the
  // full 0.3 at M >= 76 (was a binary flag at M >= 70 — a +5.2%-of-estate
  // cliff across a 0.02-pt post-ladder dial change, the same disease the
  // burnout band cured). Band centred on the old threshold, width 12 to match
  // the burnout band. The v1.3.0 climb reference (20% Living, M* ~= 80.2)
  // holds the full dividend AT EQUILIBRIUM — as does the historical 18%
  // anchor (M* 77.5) — but every life starting at M0 = 70 (wellFrac 0.5,
  // mid-band) earns only a partial dividend through the opening transient vs
  // the old binary flag (measured: balanced loses ~0.9 flourish-years,
  // absorbed by the v1.3.0 re-pin); grinder archetypes (M* <= 26) stay at
  // ~zero.
  wellLo: 64, // pt  — dividend 0 at/below
  wellHi: 76, // pt  — full dividend at/above
  wellBonus: 0.3, // pt/yr — the full flourishing health dividend
  sabHealthBase: 4, // pt/yr — sabbatical recovery at age 18...
  sabHealthSlope: 0.1, // pt/yr per yr of age — ...fades to 0 at age 58
  frailH: 25, // pt         — a hard step in observable H, currently unsurfable:
  //                          health decline is deterministic, so age is a
  //                          perfect proxy for H and a pure calendar plan
  //                          reproduces any H-reactive one (G14 carries an
  //                          H-reactive overlay bot as the canary — it starts
  //                          failing the day H gains randomness, e.g. v3 events).
  frailMult: 0.6, // —      — income multiplier while H < frailH

  // --- #23 micro cards -------------------------------------------------------
  microCardsPerYear: 3,
  microCashCapFrac: 0.02, // — of current annual income (hard cap at accept)
  microSpendPerPtLo: 0.02, // — spend card cash: morale x 2.0%...
  microSpendPerPtHi: 0.04, // — ...4.0% of current income per point
  microEarnPerPtLo: 0.004, // — earn card cash: morale x 0.4%...
  microEarnPerPtHi: 0.008, // — ...0.8% of current income per point
  microMoraleMin: 0.2, // pt
  microMoraleCap: 0.5, // pt

  // --- misc guards -----------------------------------------------------------
  livingFracMax: 1.0, // — engine clamps livingFrac to [0, livingFracMax]

  // --- #29..#30 integration --------------------------------------------------
  substep: 1 / 12, // game-yr — fixed absolute-calendar substep (G7)
};

/** Deep clone so sweeps/tuning experiments can mutate without side effects. */
export function cloneParams(overrides: Partial<Params> = {}): Params {
  const c = JSON.parse(JSON.stringify(PARAMS)) as Params;
  return Object.assign(c, overrides);
}
